import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Database, Download, Upload, Loader2, HardDrive, CheckCircle,
  AlertTriangle, Calendar, FileArchive, Trash2, RefreshCw, Shield, RotateCw,
  Clock, Settings, Save
} from "lucide-react";

// Los módulos se cargarán dinámicamente desde el backend

// ── Lectura local del índice de un archivo .back (ZIP) ──────────────────────
// Lee el directorio central del ZIP directamente desde el File (sin subirlo)
// para listar las colecciones que contiene el backup seleccionado.
async function parseBackFileEntries(file) {
  const size = file.size;
  const tailLen = Math.min(size, 65578);
  const tailBuf = await file.slice(size - tailLen, size).arrayBuffer();
  const tail = new DataView(tailBuf);
  let eocd = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) {
    if (tail.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error("No se encontró el índice del archivo (¿archivo corrupto o incompleto?)");
  let cdSize = tail.getUint32(eocd + 12, true);
  let cdOffset = tail.getUint32(eocd + 16, true);
  if (cdOffset === 0xffffffff || cdSize === 0xffffffff) {
    // ZIP64: el locator está 20 bytes antes del EOCD
    const locPos = eocd - 20;
    if (locPos >= 0 && tail.getUint32(locPos, true) === 0x07064b50) {
      const z64Off = Number(tail.getBigUint64(locPos + 8, true));
      const z64 = new DataView(await file.slice(z64Off, z64Off + 56).arrayBuffer());
      if (z64.getUint32(0, true) === 0x06064b50) {
        cdSize = Number(z64.getBigUint64(40, true));
        cdOffset = Number(z64.getBigUint64(48, true));
      }
    }
  }
  const cd = new DataView(await file.slice(cdOffset, cdOffset + cdSize).arrayBuffer());
  const dec = new TextDecoder();
  const entries = [];
  let p = 0;
  while (p + 46 <= cd.byteLength) {
    if (cd.getUint32(p, true) !== 0x02014b50) break;
    const method = cd.getUint16(p + 10, true);
    const compSize = cd.getUint32(p + 20, true);
    const nameLen = cd.getUint16(p + 28, true);
    const extraLen = cd.getUint16(p + 30, true);
    const commentLen = cd.getUint16(p + 32, true);
    const localOffset = cd.getUint32(p + 42, true);
    const name = dec.decode(new Uint8Array(cd.buffer, p + 46, nameLen));
    entries.push({ name, method, compSize, localOffset });
    p += 46 + nameLen + extraLen + commentLen;
  }
  if (!entries.length) throw new Error("El archivo no contiene entradas legibles");
  return entries;
}

// Extrae y descomprime una entrada pequeña del ZIP (ej. _metadata.json)
async function readBackFileEntry(file, entry) {
  if (entry.compSize === 0xffffffff) throw new Error("entrada zip64 no soportada");
  const head = new DataView(await file.slice(entry.localOffset, entry.localOffset + 30).arrayBuffer());
  if (head.getUint32(0, true) !== 0x04034b50) throw new Error("cabecera local inválida");
  const nameLen = head.getUint16(26, true);
  const extraLen = head.getUint16(28, true);
  const start = entry.localOffset + 30 + nameLen + extraLen;
  const blob = file.slice(start, start + entry.compSize);
  if (entry.method === 0) return await blob.text();
  if (entry.method === 8 && typeof DecompressionStream !== "undefined") {
    const ds = new DecompressionStream("deflate-raw");
    return await new Response(blob.stream().pipeThrough(ds)).text();
  }
  throw new Error("método de compresión no soportado");
}

export default function BackupPage() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedModulos, setSelectedModulos] = useState([]);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [streamDownloading, setStreamDownloading] = useState(false);
  const [backupProgress, setBackupProgress] = useState(0);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState(0);
  const [restorePhase, setRestorePhase] = useState(""); // "uploading" | "processing" | "completed" | "failed"
  const [restoreInfo, setRestoreInfo] = useState(""); // texto descriptivo (colección actual, etc.)
  const [restoreError, setRestoreError] = useState(""); // mensaje de error persistente en el modal
  const [restoreResult, setRestoreResult] = useState(null); // {colecciones, documentos, errores}
  const [restoreJobId, setRestoreJobId] = useState(""); // para que el usuario lo vea en pantalla
  const [restoreSoloColecciones, setRestoreSoloColecciones] = useState(""); // restauración selectiva (CSV, fallback manual)
  // Contenido del backup seleccionado (leído localmente del .back)
  const [backupIndex, setBackupIndex] = useState(null); // { colecciones: [{nombre, documentos}] } | { error: "..." }
  const [parsingIndex, setParsingIndex] = useState(false);
  const [seleccionRestore, setSeleccionRestore] = useState([]); // nombres de colecciones marcadas
  const [filtroColeccion, setFiltroColeccion] = useState("");
  // Progreso en vivo por colección/módulo durante la restauración
  const [restoreLive, setRestoreLive] = useState(null); // { current, processed, total, recent: [] }
  const [modulosDisponibles, setModulosDisponibles] = useState([]);
  const [loadingModulos, setLoadingModulos] = useState(true);
  
  // Estado para configuración de backups automáticos
  const [autoBackupConfig, setAutoBackupConfig] = useState({
    enabled: false,
    frequency_days: 7,
    email_notificacion: "",
    modulos: [],
    hora: "02:00",
    last_run: null,
    next_run: null
  });
  const [savingConfig, setSavingConfig] = useState(false);
  // Migración de binarios a object storage
  const [migracionInfo, setMigracionInfo] = useState(null);
  const [limpiando, setLimpiando] = useState(false);
  const [verificandoBackup, setVerificandoBackup] = useState(false);
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());

  const fetchBackups = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/backups`, getAuthHeaders());
      setBackups(res.data);
    } catch (err) {
      console.error("Error loading backups:", err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Cargar configuración de backup automático
  const fetchAutoBackupConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/backups/auto-config`, getAuthHeaders());
      if (res.data) {
        setAutoBackupConfig(res.data);
      }
    } catch (err) {
      console.error("Error loading auto backup config:", err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchBackups();
    fetchAutoBackupConfig();
    fetchModulosDisponibles();
    fetchMigracionInfo();
  }, [fetchBackups, fetchAutoBackupConfig]);

  // Estado de la migración de binarios a la nube (y polling mientras trabaja)
  const fetchMigracionInfo = async () => {
    try {
      const res = await axios.get(`${API}/backups/migracion-binarios/estado`, getAuthHeaders());
      setMigracionInfo(res.data);
      return res.data;
    } catch (err) {
      return null;
    }
  };

  useEffect(() => {
    if (!migracionInfo) return;
    const activo = migracionInfo.estado?.running || migracionInfo.limpieza?.running;
    if (!activo) return;
    const t = setTimeout(fetchMigracionInfo, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [migracionInfo]);

  const iniciarLimpiezaBase64 = async () => {
    const total = migracionInfo?.total_limpiable || 0;
    if (!window.confirm(
      `Se eliminará el campo base64 de ${total.toLocaleString()} documento(s) de MongoDB.\n\n` +
      `SOLO se borra cuando el archivo en la nube se verifica idéntico (sha256) en ese momento. ` +
      `Los archivos seguirán sirviéndose desde el almacenamiento de objetos.\n\n¿Continuar?`
    )) return;
    try {
      setLimpiando(true);
      const res = await axios.post(`${API}/backups/migracion-binarios/limpiar`, {}, getAuthHeaders());
      toast.success(res.data?.message || "Limpieza iniciada");
      setTimeout(fetchMigracionInfo, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar la limpieza");
    } finally {
      setLimpiando(false);
    }
  };

  // Prueba de restauración: valida que el último backup se puede abrir y leer completo
  const probarUltimoBackup = async () => {
    const ultimo = backups.find((b) => b.has_binary);
    if (!ultimo) {
      toast.error("No hay backups con archivo disponible para probar");
      return;
    }
    try {
      setVerificandoBackup(true);
      const res = await axios.post(`${API}/backups/${ultimo.id}/verificar`, {}, getAuthHeaders());
      toast.info(res.data?.message || "Prueba de restauración iniciada en segundo plano");
      // Polling: el trabajo corre en el servidor (backups grandes tardan varios minutos)
      const inicio = Date.now();
      const poll = async () => {
        if (Date.now() - inicio > 20 * 60 * 1000) {
          toast.error("La prueba sigue en curso; revisa el historial más tarde");
          setVerificandoBackup(false);
          fetchBackups();
          return;
        }
        try {
          const r = await axios.get(`${API}/backups`, getAuthHeaders());
          const b = (r.data || []).find((x) => x.id === ultimo.id);
          const v = b?.verificacion;
          if (v && !v.running && v.verificado_at) {
            setVerificandoBackup(false);
            fetchBackups();
            if (v.ok) {
              toast.success(`Backup íntegro: ${v.archivos} archivo(s) leídos completos, ${v.colecciones_meta} colecciones (${(v.documentos_meta || 0).toLocaleString()} documentos) en ${v.duracion_seg}s`);
            } else {
              toast.error(`La prueba de restauración FALLÓ: ${v.error}`);
            }
            return;
          }
        } catch (e) {
          // reintento silencioso
        }
        setTimeout(poll, 6000);
      };
      setTimeout(poll, 6000);
    } catch (err) {
      setVerificandoBackup(false);
      toast.error(err.response?.data?.detail || "No se pudo iniciar la prueba de restauración");
    }
  };

  // Cargar módulos disponibles desde el backend
  const fetchModulosDisponibles = async () => {
    try {
      setLoadingModulos(true);
      const res = await axios.get(`${API}/backups/modulos`, getAuthHeaders());
      setModulosDisponibles(res.data);
    } catch (err) {
      console.error("Error loading backup modules:", err);
      toast.error("Error al cargar módulos disponibles");
    } finally {
      setLoadingModulos(false);
    }
  };

  // Mapa colección -> { label, icon } del módulo al que pertenece, para mostrar
  // un nombre amigable durante la restauración en vivo.
  const collectionToModule = useMemo(() => {
    const map = {};
    (modulosDisponibles || []).forEach((m) => {
      (m.collections || []).forEach((c) => {
        if (!map[c]) map[c] = { label: m.label, icon: m.icon };
      });
    });
    return map;
  }, [modulosDisponibles]);

  const moduleLabelFor = useCallback(
    (coll) => collectionToModule[coll] || { label: null, icon: "📦" },
    [collectionToModule]
  );

  // Guardar configuración de backup automático
  const handleSaveAutoBackupConfig = async () => {
    if (autoBackupConfig.enabled && autoBackupConfig.modulos.length === 0) {
      toast.error("Seleccione al menos un módulo para el backup automático");
      return;
    }

    try {
      setSavingConfig(true);
      await axios.put(`${API}/backups/auto-config`, autoBackupConfig, getAuthHeaders());
      toast.success("Configuración guardada correctamente");
      fetchAutoBackupConfig(); // Recargar para obtener next_run actualizado
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar configuración");
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleAutoModulo = (moduloId) => {
    setAutoBackupConfig(prev => ({
      ...prev,
      modulos: prev.modulos.includes(moduloId)
        ? prev.modulos.filter(m => m !== moduloId)
        : [...prev.modulos, moduloId]
    }));
  };

  const selectAllAutoModulos = () => {
    setAutoBackupConfig(prev => ({
      ...prev,
      modulos: modulosDisponibles.map(m => m.id)
    }));
  };

  const toggleModulo = (moduloId) => {
    setSelectedModulos(prev => 
      prev.includes(moduloId) 
        ? prev.filter(m => m !== moduloId)
        : [...prev, moduloId]
    );
  };

  const selectAll = () => {
    setSelectedModulos(modulosDisponibles.map(m => m.id));
  };

  const deselectAll = () => {
    setSelectedModulos([]);
  };

  const handleCreateBackup = async () => {
    if (selectedModulos.length === 0) {
      toast.error("Seleccione al menos un módulo para respaldar");
      return;
    }

    let pollInterval = null;
    try {
      setCreatingBackup(true);
      setBackupProgress(0);

      // Iniciar el job (retorna inmediatamente con job_id, status 202)
      const startRes = await axios.post(`${API}/backups`, {
        modulos: selectedModulos,
        descripcion: `Backup de ${selectedModulos.length} módulo(s)`
      }, getAuthHeaders());

      const jobId = startRes.data.job_id;
      if (!jobId) throw new Error("Backend no devolvió job_id");

      toast.info(`Backup iniciado en segundo plano. Procesando ${selectedModulos.length} módulo(s)…`);

      // Polear cada 3 segundos hasta que termine
      await new Promise((resolve, reject) => {
        let consecutiveFailures = 0;
        pollInterval = setInterval(async () => {
          try {
            const statusRes = await axios.get(`${API}/backups/jobs/${jobId}`, getAuthHeaders());
            const job = statusRes.data;
            consecutiveFailures = 0;

            // Actualizar progress bar
            const prog = job.progress || {};
            if (prog.total > 0) {
              const pct = Math.min(95, Math.round((prog.current / prog.total) * 95));
              setBackupProgress(pct);
            }

            if (job.status === "done") {
              clearInterval(pollInterval);
              setBackupProgress(100);
              resolve(job);
            } else if (job.status === "failed") {
              clearInterval(pollInterval);
              reject(new Error(job.error || "El backup falló en el servidor"));
            }
          } catch (pollErr) {
            consecutiveFailures += 1;
            // Tolerar hasta 5 fallos seguidos (puede ser un reinicio breve)
            if (consecutiveFailures >= 5) {
              clearInterval(pollInterval);
              reject(new Error("Se perdió la conexión con el servidor mientras se generaba el backup"));
            }
          }
        }, 3000);
      });

      toast.success("Backup creado exitosamente. Lo encontrás en el historial debajo.");
      setSelectedModulos([]);
      fetchBackups();
    } catch (err) {
      if (pollInterval) clearInterval(pollInterval);
      let errorMessage = err?.response?.data?.detail || err?.message || "Error al crear backup";
      console.error("Backup error:", err);
      toast.error(errorMessage);
    } finally {
      setCreatingBackup(false);
      setBackupProgress(0);
    }
  };

  const handleStreamDownload = async () => {
    if (selectedModulos.length === 0) {
      toast.error("Seleccione al menos un módulo para respaldar");
      return;
    }
    try {
      setStreamDownloading(true);
      const res = await axios.post(`${API}/backups/stream-token`, {
        modulos: selectedModulos,
        descripcion: `Backup directo de ${selectedModulos.length} módulo(s)`
      }, getAuthHeaders());
      const token = res.data?.token;
      if (!token) throw new Error("No se recibió el token de descarga");
      const url = `${API}/backups/stream-download?token=${encodeURIComponent(token)}`;
      window.open(url, "_blank");
      toast.success("Descarga iniciada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Error al iniciar la descarga directa");
    } finally {
      setStreamDownloading(false);
    }
  };
  void handleStreamDownload;

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.back')) {
        toast.error("Solo se permiten archivos .back");
        return;
      }
      setRestoreFile(file);
      analizarBackupSeleccionado(file);
    }
  };

  // Lee el índice del .back localmente y pre-marca TODAS las colecciones.
  const analizarBackupSeleccionado = async (file) => {
    setBackupIndex(null);
    setSeleccionRestore([]);
    setFiltroColeccion("");
    setParsingIndex(true);
    try {
      const entries = await parseBackFileEntries(file);
      const jsonEntries = entries.filter((en) => en.name.endsWith('.json') && en.name !== '_metadata.json');
      if (!jsonEntries.length) throw new Error("El backup no contiene colecciones");
      // Intentar leer _metadata.json para obtener conteos de documentos (opcional)
      let countsMap = {};
      const metaEntry = entries.find((en) => en.name === '_metadata.json');
      if (metaEntry) {
        try {
          const meta = JSON.parse(await readBackFileEntry(file, metaEntry));
          (meta?.colecciones || []).forEach((c) => {
            if (c && c.nombre) countsMap[c.nombre] = c.documentos;
          });
        } catch (metaErr) {
          console.warn("[restore] no se pudo leer _metadata.json:", metaErr?.message || metaErr);
        }
      }
      const colecciones = jsonEntries
        .map((en) => en.name.replace(/\.json$/, ''))
        .sort()
        .map((nombre) => ({ nombre, documentos: countsMap[nombre] }));
      setBackupIndex({ colecciones });
      setSeleccionRestore(colecciones.map((c) => c.nombre));
      console.log(`[restore] índice leído: ${colecciones.length} colecciones en ${file.name}`);
    } catch (err) {
      console.warn("[restore] no se pudo leer el índice del backup:", err?.message || err);
      setBackupIndex({ error: err?.message || "No se pudo leer el contenido del backup" });
    } finally {
      setParsingIndex(false);
    }
  };

  const limpiarEstadoRestore = () => {
    setRestoreFile(null);
    setBackupIndex(null);
    setSeleccionRestore([]);
    setFiltroColeccion("");
    setRestoreSoloColecciones("");
  };

  // Restore CHUNKED v2: archivos grandes (varios GB) sin pegarle al límite del
  // proxy/Cloudflare. Mejoras vs v1:
  //  • Chunks de 8MB (Cloudflare timeouts ~100s; chunks chicos completan rápido)
  //  • Subida en paralelo (3 chunks simultáneos)
  //  • Reintentos automáticos por chunk (3 intentos con backoff)
  //  • Reanudación: si hay un job previo del mismo archivo, sólo sube los
  //    chunks faltantes (consulta /restore/resumable)
  //  • Reintento del /complete también
  // Todo el progreso/éxito/error se muestra DENTRO del diálogo (no se cierra
  // automáticamente). Cada paso loguea a consola con prefijo [restore].
  const handleRestore = async () => {
    if (!restoreFile) {
      toast.error("Seleccione un archivo de backup");
      return;
    }

    // Restauración selectiva: si se leyó el índice del backup, usar la selección
    // de checkboxes; si se marcan TODAS equivale a restauración completa (igual
    // que antes). Si el índice no se pudo leer, se usa el campo manual (CSV).
    let soloCsv = "";
    if (backupIndex && !backupIndex.error) {
      const total = backupIndex.colecciones.length;
      if (seleccionRestore.length === 0) {
        toast.error("Seleccione al menos una colección para restaurar");
        return;
      }
      if (seleccionRestore.length < total) {
        soloCsv = seleccionRestore.join(",");
      }
    } else if (restoreSoloColecciones && restoreSoloColecciones.trim()) {
      soloCsv = restoreSoloColecciones.trim();
    }

    if (restoreFile.size === 0) {
      toast.error("El archivo seleccionado está vacío (0 bytes). La descarga no se completó. Vuelve a descargar el backup y elimina los archivos '.crswap' incompletos.");
      setRestoreError("El archivo de backup está vacío (0 bytes). Es una descarga incompleta. Descarga el backup de nuevo antes de restaurar.");
      return;
    }

    if (!window.confirm("¿Está seguro de restaurar este backup? Los datos actuales serán reemplazados.")) {
      return;
    }

    let jobId = null;
    setRestoreError("");
    setRestoreResult(null);
    setRestoreJobId("");
    setRestoreLive(null);

    // Helper: subir un único chunk con reintentos
    const CHUNK_SIZE = 8 * 1024 * 1024; // 8 MB
    const MAX_CHUNK_RETRIES = 4;        // 4 intentos por chunk
    const PARALLEL_UPLOADS = 3;         // 3 chunks en paralelo
    const totalSize = restoreFile.size;
    const totalChunks = Math.max(1, Math.ceil(totalSize / CHUNK_SIZE));

    // Set de chunks ya subidos (para reanudación y para tracking)
    const uploadedSet = new Set();
    let retryStats = { attempts: 0 };

    const uploadOneChunk = async (i) => {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, totalSize);
      const blob = restoreFile.slice(start, end);

      let lastErr = null;
      for (let attempt = 1; attempt <= MAX_CHUNK_RETRIES; attempt++) {
        try {
          const fd = new FormData();
          fd.append("job_id", jobId);
          fd.append("chunk_index", String(i));
          fd.append("chunk", blob, `chunk_${i}`);

          const res = await axios.post(`${API}/backups/restore/chunk`, fd, {
            headers: { ...getAuthHeaders().headers },
            timeout: 180000, // 3 min por chunk (Cloudflare permite ~100s pero damos margen)
          });
          uploadedSet.add(i);
          const pct = Math.round((uploadedSet.size / totalChunks) * 50);
          setRestoreProgress(Math.min(50, pct));
          setRestoreInfo(
            `Subiendo… ${uploadedSet.size}/${totalChunks} partes` +
            (retryStats.attempts > 0 ? ` · ${retryStats.attempts} reintento(s)` : "")
          );
          return res.data;
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          // Errores no recuperables: 401/403/400/404 → abortar inmediatamente
          if ([400, 401, 403, 404].includes(status)) throw err;
          if (attempt < MAX_CHUNK_RETRIES) {
            retryStats.attempts += 1;
            const backoffMs = Math.min(15000, 2000 * Math.pow(2, attempt - 1)); // 2s, 4s, 8s
            console.warn(
              `[restore] chunk ${i + 1}/${totalChunks} falló (intento ${attempt}/${MAX_CHUNK_RETRIES}, ` +
              `status=${status || "network"}). Reintentando en ${backoffMs / 1000}s…`
            );
            setRestoreInfo(
              `Reintentando parte ${i + 1}/${totalChunks} en ${backoffMs / 1000}s ` +
              `(intento ${attempt + 1}/${MAX_CHUNK_RETRIES})…`
            );
            await new Promise((r) => setTimeout(r, backoffMs));
          }
        }
      }
      throw lastErr || new Error(`No se pudo subir la parte ${i + 1} después de ${MAX_CHUNK_RETRIES} intentos`);
    };

    try {
      setRestoring(true);
      setRestoreProgress(0);
      setRestorePhase("uploading");
      setRestoreInfo("");

      console.log(`[restore] START file=${restoreFile.name} size=${totalSize} chunks=${totalChunks}`);

      // 0) Buscar job reanudable
      let resumed = false;
      try {
        const resumeRes = await axios.get(
          `${API}/backups/restore/resumable`,
          {
            ...getAuthHeaders(),
            params: { filename: restoreFile.name, total_size: totalSize },
            timeout: 30000,
          }
        );
        if (resumeRes.data?.resumable) {
          const r = resumeRes.data;
          // Sólo reanudamos si los total_chunks coinciden (mismo tamaño de chunk en ambos lados)
          if (r.total_chunks === totalChunks) {
            const choice = window.confirm(
              `Se detectó una subida previa del mismo archivo:\n\n` +
              `• Partes ya subidas: ${r.chunks_received}/${r.total_chunks}\n` +
              `• Iniciada: ${new Date(r.created_at).toLocaleString()}\n\n` +
              `¿Reanudar la subida en lugar de empezar de cero?\n` +
              `(Aceptar = reanudar, Cancelar = empezar de cero)`
            );
            if (choice) {
              jobId = r.job_id;
              setRestoreJobId(jobId);
              (r.received_indexes || []).forEach((i) => uploadedSet.add(i));
              const pct = Math.round((uploadedSet.size / totalChunks) * 50);
              setRestoreProgress(Math.min(50, pct));
              setRestoreInfo(`Reanudando: ${uploadedSet.size}/${totalChunks} ya subidas.`);
              console.log(`[restore] RESUMING job_id=${jobId} already=${uploadedSet.size}/${totalChunks}`);
              resumed = true;
            }
          }
        }
      } catch (resumeErr) {
        console.warn("[restore] resume check failed:", resumeErr?.message || resumeErr);
      }

      // 1) Init (si no estamos reanudando)
      if (!resumed) {
        const initRes = await axios.post(
          `${API}/backups/restore/init`,
          { filename: restoreFile.name, total_size: totalSize, total_chunks: totalChunks },
          { ...getAuthHeaders(), timeout: 60000 }
        );
        jobId = initRes.data.job_id;
        setRestoreJobId(jobId);
        console.log(`[restore] init OK job_id=${jobId}`);
        setRestoreInfo(`Subiendo ${totalChunks} parte(s) de ~${Math.round(CHUNK_SIZE / 1024 / 1024)}MB…`);
      }

      // 2) Subir los chunks PENDIENTES en paralelo (PARALLEL_UPLOADS a la vez)
      const pending = [];
      for (let i = 0; i < totalChunks; i++) {
        if (!uploadedSet.has(i)) pending.push(i);
      }
      console.log(`[restore] pending chunks: ${pending.length}/${totalChunks}`);

      // Worker pool simple: cada worker toma el siguiente índice pendiente
      let cursor = 0;
      const worker = async () => {
        while (true) {
          const idx = cursor++;
          if (idx >= pending.length) return;
          const i = pending[idx];
          await uploadOneChunk(i);
        }
      };
      const workers = Array.from({ length: Math.min(PARALLEL_UPLOADS, pending.length || 1) }, () => worker());
      await Promise.all(workers);

      // 3) Complete (con reintentos y auto-recuperación de chunks faltantes)
      setRestorePhase("processing");
      setRestoreInfo("Ensamblando archivo en el servidor…");
      console.log("[restore] calling /complete");
      let completeOK = false;
      let lastCompleteErr = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const fdComplete = new FormData();
          fdComplete.append("job_id", jobId);
          if (soloCsv) {
            fdComplete.append("solo_colecciones", soloCsv);
          }
          const completeRes = await axios.post(`${API}/backups/restore/complete`, fdComplete, {
            headers: { ...getAuthHeaders().headers },
            timeout: 600000,
          });
          console.log("[restore] /complete OK", completeRes.data);
          completeOK = true;
          break;
        } catch (err) {
          lastCompleteErr = err;
          const status = err?.response?.status;
          // ✨ Auto-recuperación: si el backend reporta missing_chunks (por race
          // condition antigua o por chunks no marcados), re-subimos SOLO los
          // que faltan y reintentamos /complete. Esto evita que el usuario
          // tenga que reiniciar la subida completa.
          const detail = err?.response?.data?.detail;
          const missing = Array.isArray(detail?.missing_chunks) ? detail.missing_chunks : null;
          if (status === 400 && missing && missing.length > 0 && attempt < 3) {
            console.warn(`[restore] /complete reporta ${missing.length} chunks faltantes — re-subiendo…`);
            setRestorePhase("uploading");
            setRestoreInfo(`Re-subiendo ${missing.length} parte(s) que faltaban…`);
            // Quitar los missing del set para que uploadOneChunk los acepte
            missing.forEach((i) => uploadedSet.delete(i));
            // Subir secuencialmente con reintentos por chunk
            for (const idx of missing) {
              try {
                await uploadOneChunk(idx);
              } catch (subErr) {
                console.error(`[restore] No se pudo re-subir chunk ${idx}:`, subErr);
                throw subErr;
              }
            }
            setRestorePhase("processing");
            setRestoreInfo("Ensamblando archivo en el servidor…");
            // No incrementar attempt para no consumir un intento al recuperar
            attempt -= 1;
            continue;
          }
          if ([401, 403, 404, 409].includes(status)) throw err;
          if (attempt < 3) {
            const backoffMs = 3000 * attempt;
            console.warn(`[restore] /complete falló (intento ${attempt}/3, status=${status || "network"}). Reintento en ${backoffMs / 1000}s…`);
            setRestoreInfo(`Reintentando ensamblado en ${backoffMs / 1000}s (intento ${attempt + 1}/3)…`);
            await new Promise((r) => setTimeout(r, backoffMs));
          }
        }
      }
      if (!completeOK) throw lastCompleteErr || new Error("No se pudo ensamblar el archivo");
      setRestoreProgress(55);
      setRestoreInfo("Restaurando colecciones…");
      setRestoreLive({ current: null, processed: 0, total: 0, recent: [] });

      // 4) Poll status hasta completed/failed
      const MAX_POLL_MS = 120 * 60 * 1000; // 120 min máximo (la restauración sigue en el servidor)
      const started = Date.now();
      let lastProgress = 55;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - started > MAX_POLL_MS) {
          // No es un fallo: la restauración continúa en el servidor en segundo
          // plano. Informamos al usuario y dejamos visible el job_id.
          setRestorePhase("running_bg");
          setRestoreInfo("");
          setRestoreError(
            "La restauración sigue ejecutándose en el servidor en segundo plano. " +
            "Puede cerrar este diálogo; los datos se seguirán restaurando. " +
            "Use 'Actualizar' más tarde para ver el resultado. Job ID: " + (jobId || "")
          );
          toast.info("La restauración continúa en el servidor en segundo plano.", { duration: 12000 });
          fetchBackups();
          return;
        }
        await new Promise((r) => setTimeout(r, 3000));
        let job;
        try {
          const r = await axios.get(`${API}/backups/restore/status/${jobId}`, { ...getAuthHeaders(), timeout: 30000 });
          job = r.data;
        } catch (pollErr) {
          console.warn("[restore] poll error (reintentando):", pollErr?.message || pollErr);
          continue;
        }
        const serverProgress = job.progress ?? 0;
        const uiProgress = 50 + Math.round(serverProgress / 2);
        if (uiProgress !== lastProgress) {
          lastProgress = uiProgress;
          setRestoreProgress(uiProgress);
        }
        if (job.current_collection) {
          setRestoreInfo(
            `Restaurando: ${job.current_collection} (${job.processed_collections}/${job.total_collections})`
          );
        } else if (job.status === "assembling") {
          setRestoreInfo("Ensamblando…");
        } else if (job.status === "processing") {
          setRestoreInfo(`Procesando (${job.processed_collections || 0}/${job.total_collections || "?"} colecciones)…`);
        }
        // Progreso en vivo estructurado (colección actual + últimas restauradas)
        if (job.status === "processing" || job.current_collection) {
          const recent = (job.restored_collections || [])
            .slice(-6)
            .reverse()
            .map((c) => ({ nombre: c.nombre, documentos: c.documentos ?? 0 }));
          setRestoreLive({
            current: job.current_collection || null,
            processed: job.processed_collections || 0,
            total: job.total_collections || 0,
            recent,
          });
        }
        if (job.status === "completed") {
          setRestoreProgress(100);
          setRestorePhase("completed");
          const docs = job.total_documentos || 0;
          const cols = (job.restored_collections || []).length;
          const errs = (job.errores || []).length;
          setRestoreResult({ colecciones: cols, documentos: docs, errores: errs });
          setRestoreInfo(`Restauración completada.`);
          setRestoreLive(null);
          console.log("[restore] COMPLETED", job);
          toast.success(
            `Backup restaurado: ${cols} colección(es), ${docs} documento(s).` +
              (errs ? ` ${errs} colección(es) con error.` : ""),
            { duration: 15000 }
          );
          fetchBackups();
          break;
        }
        if (job.status === "failed") {
          throw new Error(job.error || "El restore falló en el servidor.");
        }
      }
    } catch (err) {
      console.error("[restore] ERROR:", err);
      let msg = "Error al restaurar backup";
      if (err.response) {
        const status = err.response.status;
        const detail = err.response.data?.detail;
        if (detail) msg = `Error ${status}: ${detail}`;
        else if (status === 413)
          msg = `Error 413: El chunk es demasiado grande para el servidor. Avise al administrador para aumentar el límite del proxy.`;
        else if (status === 502 || status === 504)
          msg = `Error ${status}: timeout del proxy. Reintente — el sistema ahora soporta reanudar desde el último chunk subido.`;
        else if (status === 520 || status === 521 || status === 522 || status === 524)
          msg = `Error ${status} (Cloudflare): la conexión se cortó. Cerrá este diálogo, volvé a abrirlo y reintentá: el sistema reanudará automáticamente desde el último chunk subido.`;
        else msg = `Error ${status}: ${err.response.statusText || "respuesta sin detalle"}`;
      } else if (err.code === "ECONNABORTED") {
        msg = "Tiempo de espera agotado subiendo un chunk. Reintente.";
      } else if (err.message) {
        msg = err.message.startsWith("Error") ? err.message : `Error: ${err.message}`;
      }
      setRestorePhase("failed");
      setRestoreError(msg);
      setRestoreInfo("");
      toast.error(msg, { duration: 15000 });
    } finally {
      setRestoring(false);
      // No reseteamos progress/phase aquí — se ven hasta que el usuario cierre el modal.
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm("¿Está seguro de eliminar este backup?")) return;
    
    try {
      await axios.delete(`${API}/backups/${backupId}`, getAuthHeaders());
      toast.success("Backup eliminado");
      fetchBackups();
    } catch (err) {
      toast.error("Error al eliminar backup");
    }
  };

  const handleDownloadBackup = async (backup) => {
    if (downloadingId) {
      toast.warning("Ya hay una descarga en curso. Espera a que termine.");
      return;
    }
    setDownloadingId(backup.id);
    setDownloadPct(0);
    let writable = null;
    let fileHandle = null;
    try {
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const auth = getAuthHeaders().headers.Authorization;

      // 1) Obtener tamaño y nombre exactos del backup
      const infoRes = await axios.get(`${API}/backups/${backup.id}/download-info`, getAuthHeaders());
      const totalSize = Number(infoRes.data?.size_bytes || 0);
      const filename = infoRes.data?.filename || backup.filename || `backup_${backup.id}.back`;
      if (!totalSize) throw new Error("El backup no tiene datos descargables");

      const downloadUrl = `${baseUrl}/api/backups/${backup.id}/download`;
      // Segmentos de 40MB: cada petición termina muy por debajo del límite ~100s del proxy
      const CHUNK = 40 * 1024 * 1024;
      const MAX_RETRIES = 5; // reintentos por segmento ante fallos transitorios del proxy

      // 2) ¿Soporta el navegador guardado directo a disco? (Chrome/Edge)
      if (window.showSaveFilePicker) {
        try {
          fileHandle = await window.showSaveFilePicker({ suggestedName: filename });
          writable = await fileHandle.createWritable();
        } catch (pickErr) {
          if (pickErr?.name === "AbortError") {
            // Usuario canceló el diálogo de guardar
            setDownloadingId(null);
            return;
          }
          writable = null; // cae al método en memoria
        }
      }

      const chunks = writable ? null : []; // si no hay writable, acumulamos en memoria
      let received = 0;

      // 3) Descargar por segmentos vía Range (con reintentos por segmento)
      for (let start = 0; start < totalSize; start += CHUNK) {
        const end = Math.min(start + CHUNK - 1, totalSize - 1);
        let buf = null;
        let lastErr = null;
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            const res = await fetch(downloadUrl, {
              method: "GET",
              headers: { Authorization: auth, Range: `bytes=${start}-${end}` },
            });
            if (res.status !== 206 && res.status !== 200) {
              throw new Error(`HTTP ${res.status}`);
            }
            buf = await res.arrayBuffer();
            const expected = end - start + 1;
            // Si pidió un rango pero recibió menos de lo esperado (y no es 200-completo), reintenta
            if (res.status === 206 && buf.byteLength !== expected) {
              throw new Error(`segmento incompleto (${buf.byteLength}/${expected})`);
            }
            if (res.status === 200) {
              // El servidor ignoró el Range y devolvió todo el archivo
              if (writable) { await writable.write(new Uint8Array(buf)); }
              else { chunks.push(buf); }
              received = buf.byteLength;
              setDownloadPct(100);
              start = totalSize; // forzar fin del bucle externo
              buf = null;
            }
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
            if (attempt < MAX_RETRIES) {
              await new Promise(r => setTimeout(r, 1500 * attempt)); // backoff
            }
          }
        }
        if (lastErr) throw new Error(`Error en segmento (byte ${start}): ${lastErr.message}. Descarga interrumpida.`);
        if (buf) {
          if (writable) { await writable.write(new Uint8Array(buf)); }
          else { chunks.push(buf); }
          received += buf.byteLength;
          setDownloadPct(Math.min(100, Math.round((received / totalSize) * 100)));
        }
      }

      // Validar que se recibió TODO antes de finalizar
      if (received < totalSize) {
        throw new Error(`Descarga incompleta (${received}/${totalSize} bytes). Intenta de nuevo.`);
      }

      // 4) Finalizar
      if (writable) {
        await writable.close();
        toast.success("Descarga completada y guardada en disco");
      } else {
        const blob = new Blob(chunks, { type: "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        toast.success("Descarga completada");
      }
    } catch (err) {
      // Abortar la escritura a disco para NO dejar un archivo vacío/incompleto (.crswap)
      if (writable) {
        try { await writable.abort(); } catch (_) { /* ignore */ }
      }
      let detail = null;
      let status = err.response?.status;
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          detail = JSON.parse(text)?.detail;
        } else {
          detail = err.response?.data?.detail;
        }
      } catch (_) { /* keep default */ }

      // Si el binario no existe (410), ofrecer regenerar
      if (status === 410 && backup?.modulos?.length) {
        if (window.confirm(
          `${detail || "El binario de este backup no está disponible."}\n\n¿Quieres regenerarlo ahora con los módulos originales (${backup.modulos.length}) + datos actuales?`
        )) {
          setDownloadingId(null);
          await handleRegenerateBackup(backup, /*skipConfirm=*/ true, /*thenDownload=*/ true);
          return;
        }
      }
      toast.error(detail || err.message || "Error al descargar backup");
    } finally {
      setDownloadingId(null);
      setDownloadPct(0);
    }
  };

  // Format bytes to a human readable size
  const formatBackupSize = (bytes) => {
    if (bytes === null || bytes === undefined) return "—";
    if (bytes === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    const val = bytes / Math.pow(1024, i);
    return `${val >= 10 || i === 0 ? val.toFixed(0) : val.toFixed(1)} ${units[i]}`;
  };

  const [regenerating, setRegenerating] = useState(null);
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadPct, setDownloadPct] = useState(0);

  const handleRegenerateBackup = async (backup, skipConfirm = false, thenDownload = false) => {
    if (!skipConfirm && !window.confirm(
      `Regenerar este backup?\n\nSe creará un nuevo ZIP con los mismos módulos (${backup.modulos?.length || 0}) pero con los DATOS ACTUALES (no el snapshot original del ${new Date(backup.created_at).toLocaleDateString()}).`
    )) return;
    try {
      setRegenerating(backup.id);
      const res = await axios.post(
        `${API}/backups/${backup.id}/regenerar`,
        {},
        getAuthHeaders()
      );
      toast.success(res.data?.message || "Backup regenerado");
      await fetchBackups();
      if (thenDownload) {
        // Re-fetch the backup from the refreshed list (now with gridfs_file_id) and trigger download
        setTimeout(() => handleDownloadBackup({ ...backup, has_binary: true }), 400);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al regenerar el backup");
    } finally {
      setRegenerating(null);
    }
  };

  const handleRegenerateAllMissing = async () => {
    const missing = backups.filter((b) => !b.has_binary);
    if (missing.length === 0) {
      toast.info("No hay backups sin binario para regenerar");
      return;
    }
    if (!window.confirm(
      `Regenerar ${missing.length} backup(s) sin binario?\n\nEsto puede tardar varios minutos si hay backups grandes.`
    )) return;
    try {
      setRegeneratingAll(true);
      const res = await axios.post(
        `${API}/backups/regenerar-faltantes`,
        {},
        getAuthHeaders()
      );
      const { regenerados, fallidos } = res.data || {};
      toast.success(`${regenerados || 0} regenerado(s)${fallidos ? `, ${fallidos} falló(ron)` : ""}`);
      fetchBackups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al regenerar backups faltantes");
    } finally {
      setRegeneratingAll(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="ml-0 md:ml-64 flex-1 flex items-center justify-center">
          <div className="text-center">
            <Shield className="h-16 w-16 mx-auto mb-4 text-slate-400" />
            <h2 className="text-xl font-semibold text-slate-700 dark:text-slate-300">Acceso Restringido</h2>
            <p className="text-slate-500">Solo administradores pueden acceder a esta sección</p>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="ml-0 md:ml-64 flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 p-6 overflow-auto" data-testid="backup-page">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <Database className="h-7 w-7 text-blue-600" />
                Copias de Seguridad
              </h1>
              <p className="text-slate-500 dark:text-slate-400">
                Realice y restaure backups de los módulos del sistema
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowRestoreDialog(true)}
              className="gap-2"
            >
              <Upload className="h-4 w-4" />
              Restaurar Backup
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Module Selection */}
            <div className="lg:col-span-2">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <HardDrive className="h-5 w-5" />
                        Seleccionar Módulos
                      </CardTitle>
                      <CardDescription>
                        Seleccione los módulos que desea respaldar
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchModulosDisponibles}
                        disabled={loadingModulos}
                        data-testid="refresh-modulos-btn"
                        title="Vuelve a escanear MongoDB y trae los módulos actualizados"
                      >
                        <Loader2 className={`h-3.5 w-3.5 mr-1 ${loadingModulos ? "animate-spin" : ""}`} />
                        Actualizar
                      </Button>
                      <Button variant="ghost" size="sm" onClick={selectAll}>
                        Todos
                      </Button>
                      <Button variant="ghost" size="sm" onClick={deselectAll}>
                        Ninguno
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingModulos ? (
                    <div className="flex justify-center p-8">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {modulosDisponibles.map(modulo => (
                        <div
                          key={modulo.id}
                          onClick={() => toggleModulo(modulo.id)}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedModulos.includes(modulo.id)
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                              : 'border-slate-200 dark:border-slate-600 hover:border-blue-300'
                          }`}
                        >
                          <Checkbox 
                            checked={selectedModulos.includes(modulo.id)}
                            onCheckedChange={() => toggleModulo(modulo.id)}
                          />
                          <span className="text-lg">{modulo.icon}</span>
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-medium truncate">{modulo.label}</span>
                            <span className="text-xs text-slate-500">{modulo.collection_count} colecciones</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Create Backup Button */}
                  <div className="mt-6 pt-4 border-t dark:border-slate-700">
                    {creatingBackup && (
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span>Creando backup...</span>
                          <span>{backupProgress}%</span>
                        </div>
                        <Progress value={backupProgress} className="h-2" />
                      </div>
                    )}
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={handleCreateBackup}
                      disabled={creatingBackup || streamDownloading || selectedModulos.length === 0}
                    >
                      {creatingBackup ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Crear Backup ({selectedModulos.length} módulo{selectedModulos.length !== 1 ? 's' : ''})
                    </Button>
                    <p className="text-xs text-slate-500 mt-2">
                      Genera el respaldo en el servidor y luego descárgalo desde el historial con el botón <strong>Descargar</strong> (descarga segura por segmentos, apta para respaldos de varios GB).
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Backup History */}
            <div>
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader className="flex flex-row items-start justify-between gap-2 pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileArchive className="h-5 w-5" />
                    Historial de Backups
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {backups.some((b) => b.has_binary) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={probarUltimoBackup}
                        disabled={verificandoBackup}
                        className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:border-emerald-700"
                        data-testid="probar-ultimo-backup-btn"
                        title="Descarga el último backup y verifica que se puede abrir y leer completo (CRC de todos los archivos)"
                      >
                        {verificandoBackup ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          "🧪 "
                        )}
                        {verificandoBackup ? "Probando..." : "Probar restauración"}
                      </Button>
                    )}
                    {backups.some((b) => !b.has_binary) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRegenerateAllMissing}
                        disabled={regeneratingAll}
                        className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:border-amber-700"
                        data-testid="regenerate-all-missing-btn"
                      >
                        <RotateCw className={`w-3 h-3 mr-1 ${regeneratingAll ? "animate-spin" : ""}`} />
                        {regeneratingAll ? "Regenerando..." : "Regenerar faltantes"}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-[11px] text-slate-400 mb-3">
                    Se conservan automáticamente los últimos 8 backups; los más antiguos se eliminan solos.
                  </p>
                  {backups.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileArchive className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No hay backups registrados</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {backups.map(backup => (
                        <div key={backup.id} className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg" data-testid={`backup-card-${backup.id}`}>
                          <div className="flex items-center justify-between mb-2 gap-2">
                            <Badge variant="outline" className="text-xs">
                              {backup.modulos?.length || 0} módulos
                            </Badge>
                            <Badge
                              variant="outline"
                              className="text-xs font-medium border-[#193966]/30 text-[#193966] dark:text-blue-300 dark:border-blue-300/30"
                              data-testid={`backup-size-${backup.id}`}
                              title={
                                backup.size_bytes
                                  ? `${backup.size_bytes.toLocaleString()} bytes`
                                  : "Sin datos binarios disponibles"
                              }
                            >
                              {formatBackupSize(backup.size_bytes)}
                            </Badge>
                            {!backup.has_binary && (
                              <Badge
                                className="text-[10px] bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300"
                                title="El binario no está disponible, puedes regenerarlo"
                              >
                                Sin binario
                              </Badge>
                            )}
                            {backup.verificacion?.running && (
                              <Badge
                                className="text-[10px] bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300"
                                title={`La prueba de restauración está en curso en el servidor${backup.verificacion.progreso?.archivos ? ` — ${backup.verificacion.progreso.archivos} archivo(s) leídos` : ""}${backup.verificacion.intentos > 1 ? ` (intento ${backup.verificacion.intentos})` : ""}`}
                              >
                                ⏳ Verificando…
                              </Badge>
                            )}
                            {backup.verificacion?.ok && (
                              <Badge
                                className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300"
                                data-testid={`backup-verificado-${backup.id}`}
                                title={`Prueba de restauración OK el ${new Date(backup.verificacion.verificado_at).toLocaleString()} — ${backup.verificacion.archivos} archivo(s) íntegros en ${backup.verificacion.duracion_seg}s`}
                              >
                                ✓ Verificado
                              </Badge>
                            )}
                            {backup.verificacion && !backup.verificacion.running && backup.verificacion.verificado_at && !backup.verificacion.ok && (
                              <Badge
                                className="text-[10px] bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300"
                                title={`La prueba de restauración falló: ${backup.verificacion.error}`}
                              >
                                ✗ Falló prueba
                              </Badge>
                            )}
                            <span className="text-xs text-slate-500">
                              {new Date(backup.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-300 mb-2 truncate">
                            {backup.descripcion || backup.filename}
                          </p>
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDownloadBackup(backup)}
                              disabled={downloadingId === backup.id}
                              data-testid={`download-backup-${backup.id}`}
                              title="Descargar"
                            >
                              {downloadingId === backup.id ? (
                                <span className="flex items-center gap-1 text-xs">
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  {downloadPct}%
                                </span>
                              ) : (
                                <Download className="h-3 w-3" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRegenerateBackup(backup)}
                              disabled={regenerating === backup.id}
                              className="text-amber-700 dark:text-amber-300"
                              data-testid={`regenerate-backup-${backup.id}`}
                              title="Regenerar este backup (usará los módulos originales + datos actuales)"
                            >
                              <RotateCw className={`h-3 w-3 ${regenerating === backup.id ? "animate-spin" : ""}`} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500"
                              onClick={() => handleDeleteBackup(backup.id)}
                              data-testid={`delete-backup-${backup.id}`}
                              title="Eliminar"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Info Card */}
          <Card className="mt-6 bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800 dark:text-amber-300">Importante</h4>
                  <ul className="text-sm text-amber-700 dark:text-amber-400 mt-1 space-y-1">
                    <li>• Los backups se descargan en formato .back (archivo comprimido)</li>
                    <li>• Al restaurar, los datos actuales de los módulos seleccionados serán reemplazados</li>
                    <li>• Se recomienda realizar backups periódicos de todos los módulos</li>
                    <li>• Guarde los archivos de backup en un lugar seguro</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Auto Backup Configuration */}
          <Card className="mt-6 dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="h-5 w-5 text-green-600" />
                    Copias de Seguridad Automáticas
                  </CardTitle>
                  <CardDescription>
                    Configure backups automáticos periódicos
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Label htmlFor="auto-backup-switch" className="text-sm">
                    {autoBackupConfig.enabled ? "Activado" : "Desactivado"}
                  </Label>
                  <Switch
                    id="auto-backup-switch"
                    checked={autoBackupConfig.enabled}
                    onCheckedChange={(checked) => setAutoBackupConfig(prev => ({ ...prev, enabled: checked }))}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className={`space-y-6 ${!autoBackupConfig.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                {/* Frecuencia y Hora */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block">Frecuencia</Label>
                    <Select
                      value={String(autoBackupConfig.frequency_days)}
                      onValueChange={(val) => setAutoBackupConfig(prev => ({ ...prev, frequency_days: parseInt(val) }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar frecuencia" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Cada día</SelectItem>
                        <SelectItem value="3">Cada 3 días</SelectItem>
                        <SelectItem value="7">Cada 7 días (semanal)</SelectItem>
                        <SelectItem value="14">Cada 14 días (quincenal)</SelectItem>
                        <SelectItem value="30">Cada 30 días (mensual)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-2 block">Hora de ejecución</Label>
                    <Input
                      type="time"
                      value={autoBackupConfig.hora}
                      onChange={(e) => setAutoBackupConfig(prev => ({ ...prev, hora: e.target.value }))}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Se recomienda en horario de baja actividad
                    </p>
                  </div>
                </div>

                {/* Correo de notificación */}
                <div>
                  <Label className="mb-2 block">Correo de notificación</Label>
                  <Input
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={autoBackupConfig.email_notificacion || ""}
                    onChange={(e) => setAutoBackupConfig(prev => ({ ...prev, email_notificacion: e.target.value }))}
                    data-testid="input-backup-email-notificacion"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Al completarse (o fallar) el backup programado se enviará un correo con el detalle a esta dirección. Déjalo vacío si no quieres notificaciones.
                  </p>
                </div>

                {/* Módulos para backup automático */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <Label>Módulos a respaldar</Label>
                    <Button variant="ghost" size="sm" onClick={selectAllAutoModulos}>
                      Seleccionar todos
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {modulosDisponibles.map(modulo => (
                      <div
                        key={modulo.id}
                        onClick={() => toggleAutoModulo(modulo.id)}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                          autoBackupConfig.modulos.includes(modulo.id)
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                            : 'border-slate-200 dark:border-slate-600 hover:border-green-300'
                        }`}
                      >
                        <Checkbox 
                          checked={autoBackupConfig.modulos.includes(modulo.id)}
                          onCheckedChange={() => toggleAutoModulo(modulo.id)}
                        />
                        <span>{modulo.icon}</span>
                        <span className="truncate">{modulo.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Info de último y próximo backup */}
                {autoBackupConfig.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-slate-500" />
                      <div>
                        <p className="text-xs text-slate-500">Último backup</p>
                        <p className="text-sm font-medium">
                          {autoBackupConfig.last_run 
                            ? new Date(autoBackupConfig.last_run).toLocaleString() 
                            : "Nunca ejecutado"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="h-5 w-5 text-green-500" />
                      <div>
                        <p className="text-xs text-slate-500">Próximo backup</p>
                        <p className="text-sm font-medium text-green-600">
                          {autoBackupConfig.next_run 
                            ? new Date(autoBackupConfig.next_run).toLocaleString() 
                            : "Por calcular"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Botón Guardar */}
              <div className="mt-6 pt-4 border-t dark:border-slate-700">
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={handleSaveAutoBackupConfig}
                  disabled={savingConfig}
                >
                  {savingConfig ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Guardar Configuración
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Panel: Migración de binarios a la nube */}
          {isAdmin && migracionInfo && (
            <Card className="mt-6 dark:bg-slate-800 dark:border-slate-700" data-testid="panel-migracion-binarios">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  ☁️ Archivos binarios en la nube
                  {(migracionInfo.estado?.running || migracionInfo.limpieza?.running) && (
                    <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      {migracionInfo.estado?.running ? "Migrando…" : "Limpiando…"}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Imágenes y archivos adjuntos viven en el almacenamiento de objetos; MongoDB conserva solo metadatos.
                  Los backups excluyen estos binarios y son mucho más livianos.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {(() => {
                  const pend = migracionInfo.pendientes_por_coleccion || {};
                  const migr = migracionInfo.migrados_por_coleccion || {};
                  const totalPend = Object.values(pend).reduce((a, b) => a + Math.max(0, b), 0);
                  const totalMigr = Object.values(migr).reduce((a, b) => a + Math.max(0, b), 0);
                  const limpiable = migracionInfo.total_limpiable || 0;
                  return (
                    <>
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-3">
                          <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400" data-testid="migracion-total-migrados">{totalMigr.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">En la nube</p>
                        </div>
                        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
                          <p className="text-2xl font-bold text-amber-700 dark:text-amber-400" data-testid="migracion-total-pendientes">{totalPend.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">Pendientes de migrar</p>
                        </div>
                        <div className="rounded-lg bg-slate-50 dark:bg-slate-700/40 p-3">
                          <p className="text-2xl font-bold text-slate-700 dark:text-slate-300" data-testid="migracion-total-limpiable">{limpiable.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">Base64 por limpiar en Mongo</p>
                        </div>
                      </div>
                      {totalPend + totalMigr > 0 && (
                        <div>
                          <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                            <div
                              className="h-2 rounded-full bg-emerald-500 transition-all"
                              style={{ width: `${Math.round((totalMigr / Math.max(1, totalMigr + totalPend)) * 100)}%` }}
                            />
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {Math.round((totalMigr / Math.max(1, totalMigr + totalPend)) * 100)}% migrado
                            {migracionInfo.estado?.coleccion_actual ? ` — procesando: ${migracionInfo.estado.coleccion_actual}` : ""}
                            {migracionInfo.estado?.errores ? ` — errores: ${migracionInfo.estado.errores}` : ""}
                          </p>
                        </div>
                      )}
                      <div className="text-xs text-slate-500 space-y-0.5">
                        {Object.keys(migr).map((c) => (
                          <div key={c} className="flex justify-between">
                            <span>{c}</span>
                            <span>
                              {migr[c] < 0 ? "?" : migr[c].toLocaleString()} en nube{pend[c] ? ` · ${pend[c] < 0 ? "?" : pend[c]} pendiente(s)` : ""}
                            </span>
                          </div>
                        ))}
                      </div>
                      {migracionInfo.limpieza?.finished_at && !migracionInfo.limpieza?.running && (
                        <p className="text-xs text-emerald-600">
                          Última limpieza: {migracionInfo.limpieza.limpiados.toLocaleString()} documento(s) liberados
                          (~{Math.round((migracionInfo.limpieza.bytes_liberados || 0) / (1024 * 1024))} MB de base64)
                          {migracionInfo.limpieza.no_coinciden ? ` — ${migracionInfo.limpieza.no_coinciden} no coincidieron y NO se borraron` : ""}
                        </p>
                      )}
                      <div className="flex items-center gap-2">
                        {totalPend > 0 && !migracionInfo.estado?.running && (
                          <Button
                            data-testid="btn-iniciar-migracion"
                            size="sm"
                            disabled={migracionInfo.limpieza?.running}
                            onClick={async () => {
                              try {
                                const r = await axios.post(`${API}/backups/migracion-binarios/iniciar`, {}, getAuthHeaders());
                                toast.success(r.data?.message || "Migración iniciada");
                                setTimeout(fetchMigracionInfo, 1500);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Error al iniciar la migración");
                              }
                            }}
                          >
                            Iniciar migración ahora
                          </Button>
                        )}
                        <Button
                          data-testid="btn-limpiar-base64"
                          variant="outline"
                          size="sm"
                          disabled={limpiando || limpiable === 0 || migracionInfo.estado?.running || migracionInfo.limpieza?.running}
                          onClick={iniciarLimpiezaBase64}
                        >
                          {limpiando || migracionInfo.limpieza?.running ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : null}
                          Liberar espacio en Mongo (borrar base64 verificado)
                        </Button>
                        <Button variant="ghost" size="sm" onClick={fetchMigracionInfo} data-testid="btn-refrescar-migracion">
                          Actualizar
                        </Button>
                      </div>
                      <p className="text-[11px] text-slate-400">
                        La limpieza solo elimina el base64 de documentos cuyo archivo en la nube se verifica idéntico (sha256) al momento de borrar.
                      </p>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Restore Dialog */}
        <Dialog
          open={showRestoreDialog}
          onOpenChange={(open) => {
            // Prevenir cierre accidental durante la restauración.
            if (!open && restoring) {
              if (!window.confirm("¿Cancelar visualización? La restauración seguirá corriendo en el servidor.")) {
                return;
              }
            }
            setShowRestoreDialog(open);
          }}
        >
          <DialogContent className="max-w-md dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-blue-600" />
                Restaurar Backup
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Bloque de selección de archivo (sólo si NO hay job activo y NO hay resultado) */}
              {!restoring && !restoreResult && restorePhase !== "failed" && (
                <>
                  <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".back"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="backup-file"
                    />
                    <label htmlFor="backup-file" className="cursor-pointer">
                      <FileArchive className="h-12 w-12 mx-auto mb-3 text-slate-400" />
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        {restoreFile ? restoreFile.name : "Click para seleccionar archivo .back"}
                      </p>
                    </label>
                  </div>

                  {restoreFile && (
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-sm text-green-700 dark:text-green-300 break-all">
                        Archivo seleccionado: {restoreFile.name}
                        {" "}
                        <span className="text-xs text-green-600">
                          ({(restoreFile.size / 1024 / 1024).toFixed(1)} MB)
                        </span>
                      </span>
                    </div>
                  )}

                  {/* Restauración selectiva: lista de colecciones del backup */}
                  {parsingIndex && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400" data-testid="restore-index-loading">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Leyendo contenido del backup…
                    </div>
                  )}

                  {!parsingIndex && backupIndex && !backupIndex.error && (
                    <div className="space-y-2" data-testid="restore-colecciones-panel">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Restauración selectiva
                        </label>
                        <span className="text-xs text-slate-500 dark:text-slate-400" data-testid="restore-seleccion-count">
                          {seleccionRestore.length} de {backupIndex.colecciones.length} seleccionadas
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setSeleccionRestore(backupIndex.colecciones.map((c) => c.nombre))}
                          data-testid="restore-seleccionar-todas-btn"
                        >
                          Seleccionar todas
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setSeleccionRestore([])}
                          data-testid="restore-deseleccionar-todas-btn"
                        >
                          Deseleccionar todas
                        </Button>
                      </div>
                      <Input
                        value={filtroColeccion}
                        onChange={(e) => setFiltroColeccion(e.target.value)}
                        placeholder="Buscar colección…"
                        className="h-8 text-sm"
                        data-testid="restore-filtro-coleccion-input"
                      />
                      <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-600 divide-y divide-slate-100 dark:divide-slate-700" data-testid="restore-colecciones-lista">
                        {backupIndex.colecciones
                          .filter((c) => !filtroColeccion.trim() || c.nombre.toLowerCase().includes(filtroColeccion.trim().toLowerCase()))
                          .map((c) => {
                            const checked = seleccionRestore.includes(c.nombre);
                            const mod = moduleLabelFor(c.nombre);
                            return (
                              <label
                                key={c.nombre}
                                className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50"
                                data-testid={`restore-coleccion-item-${c.nombre}`}
                              >
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={() =>
                                    setSeleccionRestore((prev) =>
                                      prev.includes(c.nombre)
                                        ? prev.filter((n) => n !== c.nombre)
                                        : [...prev, c.nombre]
                                    )
                                  }
                                  data-testid={`restore-coleccion-checkbox-${c.nombre}`}
                                />
                                <span className="text-sm">{mod.icon}</span>
                                <span className="flex-1 min-w-0">
                                  <span className="block text-xs font-mono text-slate-700 dark:text-slate-200 break-all">{c.nombre}</span>
                                  {mod.label && (
                                    <span className="block text-[10px] text-slate-400 dark:text-slate-500 truncate">{mod.label}</span>
                                  )}
                                </span>
                                {c.documentos !== undefined && c.documentos !== null && (
                                  <span className="text-[10px] text-slate-400 dark:text-slate-500 whitespace-nowrap">
                                    {c.documentos} docs
                                  </span>
                                )}
                              </label>
                            );
                          })}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Con <strong>todas</strong> marcadas se restaura el backup completo. Si deseleccionás
                        alguna, se restaurarán <strong>SOLO</strong> las marcadas de forma{" "}
                        <strong>no destructiva</strong> (agrega/actualiza sin borrar el resto).
                      </p>
                    </div>
                  )}

                  {/* Fallback manual: solo si no se pudo leer el índice del backup */}
                  {!parsingIndex && restoreFile && backupIndex?.error && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Restauración selectiva (opcional)
                      </label>
                      <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="restore-index-error">
                        No se pudo leer el contenido del backup ({backupIndex.error}). Podés escribir los nombres manualmente.
                      </p>
                      <input
                        type="text"
                        value={restoreSoloColecciones}
                        onChange={(e) => setRestoreSoloColecciones(e.target.value)}
                        placeholder="Ej: articulos_envios_detalle"
                        className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="restore-solo-colecciones-input"
                      />
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Dejá vacío para restaurar el backup completo. Si escribís uno o más
                        nombres de colección (separados por coma), se restaurarán <strong>SOLO</strong> esas,
                        de forma <strong>no destructiva</strong> (agrega/actualiza sin borrar el resto).
                      </p>
                    </div>
                  )}

                  {(() => {
                    const indexOk = backupIndex && !backupIndex.error;
                    const total = indexOk ? backupIndex.colecciones.length : 0;
                    const esSelectivo = indexOk
                      ? seleccionRestore.length > 0 && seleccionRestore.length < total
                      : Boolean(restoreSoloColecciones && restoreSoloColecciones.trim());
                    const sinSeleccion = indexOk && seleccionRestore.length === 0;
                    if (sinSeleccion) {
                      return (
                        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg" data-testid="restore-sin-seleccion-notice">
                          <p className="text-sm text-amber-700 dark:text-amber-300">
                            <strong>Sin selección:</strong> marcá al menos una colección para poder restaurar.
                          </p>
                        </div>
                      );
                    }
                    if (esSelectivo) {
                      return (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg" data-testid="restore-selective-notice">
                          <p className="text-sm text-blue-700 dark:text-blue-300">
                            <strong>Modo selectivo:</strong> se restaurará(n) solo{" "}
                            <span className="font-mono break-all">
                              {indexOk ? `${seleccionRestore.length} colección(es) marcada(s)` : restoreSoloColecciones.trim()}
                            </span>{" "}
                            sin afectar las demás colecciones.
                          </p>
                        </div>
                      );
                    }
                    return (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <p className="text-sm text-red-700 dark:text-red-300">
                          <strong>Advertencia:</strong> Esta acción reemplazará los datos actuales con los del backup.
                        </p>
                      </div>
                    );
                  })()}
                </>
              )}

              {/* Barra de progreso mientras está corriendo */}
              {restoring && (
                <div className="space-y-3" data-testid="restore-progress-container">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-600 dark:text-slate-300 font-medium">
                      {restorePhase === "uploading" && "Subiendo archivo por partes…"}
                      {restorePhase === "processing" && "Restaurando módulos…"}
                      {!restorePhase && "Iniciando…"}
                    </span>
                    <span className="font-bold text-blue-600 text-base">{restoreProgress}%</span>
                  </div>
                  <Progress value={restoreProgress} className="h-3" data-testid="restore-progress-bar" />

                  {/* Panel en vivo: módulo/colección actual + contador */}
                  {restoreLive && restorePhase === "processing" ? (
                    <div className="rounded-lg border border-blue-100 dark:border-blue-900/40 bg-blue-50/60 dark:bg-blue-900/10 p-3 space-y-2" data-testid="restore-live-panel">
                      {restoreLive.current ? (
                        <div className="flex items-center gap-2">
                          <span className="text-lg leading-none animate-pulse">{moduleLabelFor(restoreLive.current).icon}</span>
                          <div className="min-w-0">
                            {moduleLabelFor(restoreLive.current).label && (
                              <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 truncate" data-testid="restore-live-module">
                                {moduleLabelFor(restoreLive.current).label}
                              </p>
                            )}
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 break-all font-mono" data-testid="restore-live-collection">
                              {restoreLive.current}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500">Preparando restauración…</p>
                      )}
                      {restoreLive.total > 0 && (
                        <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
                          <span>Colecciones</span>
                          <span className="font-semibold tabular-nums" data-testid="restore-live-counter">
                            {restoreLive.processed}/{restoreLive.total}
                          </span>
                        </div>
                      )}
                      {restoreLive.recent && restoreLive.recent.length > 0 && (
                        <div className="pt-1 border-t border-blue-100 dark:border-blue-900/40">
                          <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Recién restauradas</p>
                          <div className="flex flex-wrap gap-1" data-testid="restore-live-recent">
                            {restoreLive.recent.map((c) => (
                              <span
                                key={c.nombre}
                                className="inline-flex items-center gap-1 text-[10px] bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded"
                                title={`${c.documentos.toLocaleString()} documento(s)`}
                              >
                                ✓ {c.nombre}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    restoreInfo && (
                      <p className="text-xs text-slate-600 dark:text-slate-300 break-all" data-testid="restore-progress-info">
                        {restoreInfo}
                      </p>
                    )
                  )}

                  <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                    ⚠ No cierre esta ventana hasta que termine. Un backup grande puede tardar varios minutos.
                  </p>
                  {restoreJobId && (
                    <p className="text-xs text-slate-400 break-all">Job: {restoreJobId}</p>
                  )}
                </div>
              )}

              {/* Banner de ÉXITO persistente (queda hasta que el usuario cierre el modal) */}
              {restoreResult && !restoring && (
                <div
                  className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
                  data-testid="restore-success-banner"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-green-800 dark:text-green-200">
                        ✅ Backup restaurado correctamente
                      </h4>
                      <ul className="mt-2 text-xs text-green-700 dark:text-green-300 space-y-0.5">
                        <li>• Colecciones restauradas: <strong>{restoreResult.colecciones}</strong></li>
                        <li>• Documentos restaurados: <strong>{restoreResult.documentos.toLocaleString()}</strong></li>
                        {restoreResult.errores > 0 && (
                          <li className="text-amber-700">• Colecciones con errores: {restoreResult.errores}</li>
                        )}
                      </ul>
                      <p className="mt-3 text-xs text-green-700 dark:text-green-300">
                        💡 Refresque la página (F5) o vuelva a abrir el módulo correspondiente para ver los datos restaurados.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Banner de ERROR persistente */}
              {restoreError && !restoring && (
                restorePhase === "running_bg" ? (
                  <div
                    className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
                    data-testid="restore-running-bg-banner"
                  >
                    <div className="flex items-start gap-3">
                      <RefreshCw className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-bold text-blue-800 dark:text-blue-200">
                          ⏳ La restauración continúa en el servidor
                        </h4>
                        <p className="mt-2 text-xs text-blue-700 dark:text-blue-300 break-words">
                          {restoreError}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                <div
                  className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800"
                  data-testid="restore-error-banner"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-red-800 dark:text-red-200">
                        ❌ La restauración falló
                      </h4>
                      <p className="mt-2 text-xs text-red-700 dark:text-red-300 break-words">
                        {restoreError}
                      </p>
                      {restoreJobId && (
                        <p className="mt-2 text-[10px] text-red-500 break-all">
                          Job ID (para soporte): {restoreJobId}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                )
              )}
            </div>

            <DialogFooter>
              {/* Mientras corre: sólo se ve la barra. No hay botones de cancelar. */}
              {!restoring && (
                <>
                  {(restoreResult || restoreError) ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowRestoreDialog(false);
                        limpiarEstadoRestore();
                        setRestoreResult(null);
                        setRestoreError("");
                        setRestoreProgress(0);
                        setRestorePhase("");
                        setRestoreInfo("");
                        setRestoreJobId("");
                      }}
                      data-testid="restore-close-btn"
                    >
                      Cerrar
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => { setShowRestoreDialog(false); limpiarEstadoRestore(); }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleRestore}
                        disabled={!restoreFile || parsingIndex || (backupIndex && !backupIndex.error && seleccionRestore.length === 0)}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="restore-backup-submit-btn"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Restaurar
                      </Button>
                    </>
                  )}
                </>
              )}
              {restoring && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Procesando…
                </div>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
